// src/pages/LeadsMobileMapView.js
import React, { useMemo, useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { 
  Box, 
  Button, 
  Chip,
  Typography, 
  Drawer,
  Paper, 
  Card, 
  CardContent, 
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import NavigationIcon from "@mui/icons-material/Navigation";
import PushPinIcon from "@mui/icons-material/PushPin";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TuneIcon from "@mui/icons-material/Tune";
import { LEAD_STATUS_DEFINITIONS } from "../constants/leadStatuses";
import { formatStateAbbr } from "../constants/usStates";
import LeadStatusIcon from "../components/LeadStatusIcon";
import MapIcon from "@mui/icons-material/Map";
import RouteIcon from "@mui/icons-material/Route";
import STATUS_COLORS from "../utils/statusColors";
import L from "leaflet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import RequiredChoiceDialog, { focusRequiredField } from "../components/RequiredChoiceDialog";
import API from "../utils/api";
import { formatPhone } from "../utils/formatPhone";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";
import { emitLeadUpdate } from "../utils/leadSync";
import SaveRouteModal from "../components/SaveRouteModal";
import useCurrentPosition from "../hooks/useCurrentPosition";
import { useInvalidateMapOnOrientation } from "../hooks/useInvalidateMapOnOrientation";
import MapViewportFetcher from "../components/leads/MapViewportFetcher";
import MapLastViewPersistence from "../components/leads/MapLastViewPersistence";
import MapHqFallback from "../components/leads/MapHqFallback";
import ClusteredLeadMarkers from "../components/leads/ClusteredLeadMarkers";
import { getCachedMarkerIcon } from "../utils/leadsMapPinIcons";
import { getLeadsMapMobileClusterOptions, getUserLocationMinZoom, USER_LOCATION_LOCATE_ZOOM, USER_LOCATION_ACCURACY_PATH_OPTIONS } from "../utils/leadsMapClustering";
import UserLocationMapLayers from "../components/leads/UserLocationMapLayers";
import { createUserLocationIcon } from "../utils/userLocationMarker";
import { LEADS_MAP_CONTINENTAL_US, leadHasMapCoordinates } from "../utils/leadsMapInitialView";
import {
  buildNavigationDestination,
  getProviderOptions,
  getSavedNavProvider,
  openNavigation,
  providerLabel,
  saveNavProvider,
} from "../utils/navigationApps";

function mobileDrawerProposalAction(proposals) {
  const list = Array.isArray(proposals) ? proposals : [];
  const latest = list.find((proposal) => proposal?.status && proposal.status !== "draft");
  if (latest?.status === "sent") {
    return {
      label: "Pending proposal",
      icon: "timer",
      bgcolor: "#f97316",
      hover: "#ea580c",
    };
  }
  if (latest?.status === "accepted") {
    return {
      label: "Accepted proposal",
      icon: "check",
      bgcolor: "#16a34a",
      hover: "#15803d",
    };
  }
  if (latest?.status === "declined") {
    return {
      label: "Declined proposal",
      icon: "declined",
      bgcolor: "#dc2626",
      hover: "#b91c1c",
    };
  }
  return {
    label: "Create proposal",
    icon: "add",
    bgcolor: "#0874f9",
    hover: "#0667df",
  };
}

const MOBILE_USER_LOCATION_ICON = createUserLocationIcon({
  pulsing: true,
  pulseStrength: "mobile",
});



function MapZoomListener({ userZoomedRef, programmaticZoomRef, viewportFetchEnabled = false }) {
  const map = useMap();
  
  useEffect(() => {
    const handleZoomStart = (e) => {
      if (e.originalEvent && e.originalEvent.isTrusted && !programmaticZoomRef.current) {
        userZoomedRef.current = true;
        // In viewport mode, keep user zoom "sticky" so FitBoundsOnData does not fight bbox loads.
        if (viewportFetchEnabled) return;
        setTimeout(() => {
          userZoomedRef.current = false;
        }, 10000);
      }
    };
    
    map.on("zoomstart", handleZoomStart);
    
    return () => {
      map.off("zoomstart", handleZoomStart);
    };
  }, [map, userZoomedRef, programmaticZoomRef, viewportFetchEnabled]);
  
  return null;
}




const getPinColor = (status = "New", notes = "") => {
  const normalized = status.trim().toLowerCase();
  
  // Special handling for "New" status with specific note patterns
  if (normalized === "new" && notes) {
    const notesLower = notes.toLowerCase();
    
    if (notesLower.includes("adt")) {
      return "#F5D327"; // Yellow
    }
    if (notesLower.includes("title company") || notesLower.includes("realtor")) {
      return "#A627F5"; // Purple
    }
    if (notesLower.includes("builder relationship")) {
      return "#F527C8"; // Pink
    }
  }
  
  // Default status color lookup
  const match = Object.entries(STATUS_COLORS).find(
    ([key]) => key.toLowerCase() === normalized
  );
  return match ? match[1] : "#42A5F5";
};

// ---------------- MAP HELPERS ----------------
function FitBoundsOnData({ leads, userZoomedRef, initialFitRef, fitModeRef, suppressAutoFitUntilRef, didFitLeadsRef }) {
  const map = useMap();

  useEffect(() => {
    if (suppressAutoFitUntilRef?.current && Date.now() < suppressAutoFitUntilRef.current) return;
    if (userZoomedRef.current) return;
    if (!initialFitRef.current) return;

    if (!leads || leads.length === 0) return;

    const validLeads = leads.filter(leadHasMapCoordinates);

    if (!validLeads.length) return;

    const bounds = L.latLngBounds(
      validLeads.map((l) => [
        l.latitude || l.lat,
        l.longitude || l.lng,
      ])
    );

    if (bounds.isValid()) {
      initialFitRef.current = false;
      if (didFitLeadsRef) didFitLeadsRef.current = true;
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: fitModeRef.current === "filtered" ? 13 : 10,
      });
    }
  }, [leads, map, userZoomedRef, initialFitRef, fitModeRef, suppressAutoFitUntilRef, didFitLeadsRef]);

  return null;
}



function CenterOnSelectedLead({ selectedLeads, focusLead = null, userZoomedRef, programmaticZoomRef, zoomLevel = null, onZoomApplied }) {
  const map = useMap();
  const skipDefaultAfterViewOnMapRef = useRef(false);

  useEffect(() => {
    const focusLat = focusLead?.latitude ?? focusLead?.lat;
    const focusLng = focusLead?.longitude ?? focusLead?.lng;
    const forced =
      focusLead &&
      focusLat != null &&
      focusLng != null &&
      typeof zoomLevel === "number" &&
      zoomLevel >= 1 &&
      zoomLevel <= 18;

    if (!forced && skipDefaultAfterViewOnMapRef.current) {
      skipDefaultAfterViewOnMapRef.current = false;
      return;
    }
    if (!forced && userZoomedRef.current) return;
    if (!forced && programmaticZoomRef.current) return;

    const lead = forced ? focusLead : selectedLeads?.length === 1 ? selectedLeads[0] : null;
    if (!lead) return;

    const lat = lead.latitude ?? lead.lat;
    const lng = lead.longitude ?? lead.lng;
    if (lat == null || lng == null) return;

    const zoom = forced ? zoomLevel : 11;

    const runSetView = () => {
      try {
        if (!map.getContainer) return;
        const container = map.getContainer();
        if (container) void container.offsetHeight;
        map.invalidateSize(true);
        map.setView([lat, lng], zoom, {
          animate: true,
          duration: 0.7,
        });
        if (forced) {
          userZoomedRef.current = true;
          skipDefaultAfterViewOnMapRef.current = true;
          onZoomApplied?.();
        }
        setTimeout(() => {
          programmaticZoomRef.current = false;
        }, 500);
      } catch {
        // map may have unmounted
      }
    };

    const isPWA =
      typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone);

    if (isPWA) {
      const t = setTimeout(runSetView, 350);
      return () => clearTimeout(t);
    }

    runSetView();
  }, [selectedLeads, focusLead, map, userZoomedRef, programmaticZoomRef, zoomLevel, onZoomApplied]);

  return null;
}


function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: onMapClick,
  });
  return null;
}

function BoxSelectHandler({ enabled, onStart, onMove, onEnd }) {
  const map = useMap();
  
  useEffect(() => {
    if (!enabled) {
      map.dragging.enable();
      return;
    }

    map.dragging.disable();

    const container = map.getContainer();
    
    // Mouse event handlers
    const handleMouseDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      onStart({ target: map, originalEvent: e });
    };

    const handleMouseMove = (e) => {
      if (enabled) {
        e.preventDefault();
        e.stopPropagation();
      }
      onMove({ target: map, originalEvent: e });
    };

    const handleMouseUp = (e) => {
      if (enabled) {
        e.preventDefault();
        e.stopPropagation();
      }
      onEnd({ target: map, originalEvent: e });
    };

    // Touch event handlers (for mobile)
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return; // Only handle single touch
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      // Create a synthetic mouse event for compatibility
      const syntheticEvent = {
        ...touch,
        clientX: touch.clientX,
        clientY: touch.clientY,
        button: 0,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
      };
      onStart({ target: map, originalEvent: syntheticEvent });
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const syntheticEvent = {
        ...touch,
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
      };
      onMove({ target: map, originalEvent: syntheticEvent });
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.changedTouches[0] || (e.touches.length > 0 ? e.touches[0] : null);
      if (touch) {
        const syntheticEvent = {
          ...touch,
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => e.preventDefault(),
          stopPropagation: () => e.stopPropagation(),
        };
        onEnd({ target: map, originalEvent: syntheticEvent });
      }
    };

    // Add mouse event listeners
    container.addEventListener('mousedown', handleMouseDown, { passive: false });
    container.addEventListener('mousemove', handleMouseMove, { passive: false });
    container.addEventListener('mouseup', handleMouseUp, { passive: false });

    // Add touch event listeners (CRITICAL for mobile)
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      map.dragging.enable();
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [enabled, map, onStart, onMove, onEnd]);

  return null;
}

function SelectionBoxOverlay({ bounds }) {
  const map = useMap();
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!bounds) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const sw = map.latLngToContainerPoint(bounds.getSouthWest());
      const ne = map.latLngToContainerPoint(bounds.getNorthEast());
      
      setRect({
        left: Math.min(sw.x, ne.x),
        top: Math.min(sw.y, ne.y),
        width: Math.abs(ne.x - sw.x),
        height: Math.abs(ne.y - sw.y),
      });
    };

    updateRect();
    map.on('move', updateRect);
    map.on('zoom', updateRect);

    return () => {
      map.off('move', updateRect);
      map.off('zoom', updateRect);
    };
  }, [bounds, map]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        border: '2px dashed #0057A4',
        backgroundColor: 'rgba(0, 87, 164, 0.1)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    />
  );
}

// ---------------- MARKER WITH DYNAMIC ICON ----------------
function DynamicMarker({ lead, isSelected, isDrawerSelected, isDisabled, handleMarkerClick }) {
  const lat = lead.latitude || lead.lat;
  const lng = lead.longitude || lead.lng;
  const color = getPinColor(lead.status, lead.notes);
  const isSelectedOrDrawer = isSelected || isDrawerSelected;

  // Create icon based on current state - useMemo to ensure it updates when props change
  // Hooks must be called before any early returns
  const icon = useMemo(() => {
    if (isDisabled) {
      return getCachedMarkerIcon("disabled", color);
    }
    if (isSelectedOrDrawer) {
      return getCachedMarkerIcon("selected", color);
    }
    return getCachedMarkerIcon("normal", color);
  }, [isDisabled, isSelectedOrDrawer, color]);

  // Don't render if coordinates are invalid (check after hooks)
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
    return null;
  }

  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      eventHandlers={{
        click: () => handleMarkerClick(lead),
      }}
      opacity={isDisabled ? 0.5 : 1}
    />
  );
}

// ---------------- MAIN COMPONENT ----------------
const LeadsMobileMapView = ({
  filteredLeads,
  selectedLeads,
  viewLeadZoom = null,
  viewOnMapLead = null,
  highlightedLeadId = null,
  onViewLeadZoomApplied,
  handleMarkerClick,
  legendOpen,
  setLegendOpen,
  mapRef,
  userZoomedRef,
  initialFitRef,
  fitModeRef,
  selectedStatusFilters,
  isStatusSelected,
  handleStatusFilterToggle,
  handleMobileClearAll,
  toggleBoxSelect,
  boxSelectMode,
  selectionBox,
  handleBoxSelectStart,
  handleBoxSelectMove,
  handleBoxSelectEnd,
  handleClearRoute,
  handleSaveRouteClick,
  showRouteBuilder,
  setShowRouteBuilder,
  selectedLeadForDrawer,
  setSelectedLeadForDrawer,
  handleRemoveFromRoute,
  routeCoordinates = [],
  showSaveModal = false,
  setShowSaveModal,
  token,
  canSaveRoute,
  setSelectedLeads,
  leads, // Unfiltered leads for territory filtering
  territories = [], // Territories list
  selectedTerritories = [], // Selected territory IDs
  setSelectedTerritories, // Function to update selected territories
  role, // User role for permission check
  canViewAdditionalNotes = true,
  soldDateFilter, // Lead date filter state (created_at)
  setSoldDateFilter, // Function to update lead date filter
  soldDateRange, // Lead date range state
  setSoldDateRange, // Function to update lead date range
  lastThreeUploadDates = [], // Last 3 distinct upload dates from parent
  formatLeadDateLabel = (d) => d, // Format YYYY-MM-DD for display
  viewportFetchEnabled = false,
  territoryIdForViewport = null,
  viewportRefreshTick = 0,
  applyMapData,
  setLoading,
  initialMapCenter = LEADS_MAP_CONTINENTAL_US.center,
  initialMapZoom = LEADS_MAP_CONTINENTAL_US.zoom,
  mapLayoutKey = 0,
  persistLeadsMapLastView = false,
  persistLeadsMapUserId = null,
  mobileClusterEnabled = false,
  loading = false,
  companyHqView = null,
  restoredLastViewRef,
  didFitLeadsRef,
  ignoreNextAutoFitRef,
  startupGpsResolved = false,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapLeadRestoredRef = useRef(null);
  const theme = useTheme();
  const isPhoneWidth = useMediaQuery("(max-width: 767.95px)");
  const mobileClusterActive = mobileClusterEnabled && !boxSelectMode;
  const mobileClusterOptions = useMemo(
    () => getLeadsMapMobileClusterOptions(isPhoneWidth),
    [isPhoneWidth]
  );
  const userLocationMinZoom = useMemo(
    () => getUserLocationMinZoom(isPhoneWidth),
    [isPhoneWidth]
  );
  const { position: currentPosition, error: geoError } = useCurrentPosition();
  const zoomingToLocationRef = React.useRef(false);
  const programmaticZoomRef = useRef(false);
  const suppressAutoFitUntilRef = useRef(0);
  const lastKnownGoodPositionRef = useRef(null);
  const hasHandledLocationGrantRef = useRef(false);

  // Multi-pass + visualViewport: see useInvalidateMapOnOrientation (PWA rotation / safe-area lag)
  useInvalidateMapOnOrientation(mapRef);

// Re-trigger map fit when territory selection changes.
// useLayoutEffect so refs are set before FitBoundsOnData's useEffect runs (fixes territory-select zoom).
useLayoutEffect(() => {
  if (!mapRef.current) return;

  // Set zoom mode
  if (selectedTerritories.length > 0) {
    fitModeRef.current = "filtered";
  } else {
    fitModeRef.current = "global";
  }

  // Viewport/bbox mode: territory refit is handled in Leads.js (bootstrap fetch) or explicit reset actions.
  // Re-arming initialFit here caused FitBounds loops as bbox loads appended leads.
  if (viewportFetchEnabled) {
    return;
  }

  // Avoid territory/filter refits clobbering a recent explicit "my location" action
  // or a restored last-view camera on first primary-territory seed.
  if (ignoreNextAutoFitRef?.current) {
    ignoreNextAutoFitRef.current = false;
    return;
  }
  if (Date.now() >= suppressAutoFitUntilRef.current) {
    userZoomedRef.current = false;
    initialFitRef.current = true;
  }

}, [selectedTerritories, viewportFetchEnabled]);

useEffect(() => {
  if (!currentPosition) return;
  const { lat, lng, accuracy } = currentPosition;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
  if (!Number.isFinite(accuracy)) return;
  lastKnownGoodPositionRef.current = currentPosition;
}, [currentPosition]);

const isValidCoordinatePair = useCallback((lat, lng) => {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}, []);

const getLeadCoordinates = useCallback((lead) => {
  const lat = lead.latitude ?? lead.lat;
  const lng = lead.longitude ?? lead.lng;
  return isValidCoordinatePair(lat, lng) ? [lat, lng] : null;
}, [isValidCoordinatePair]);

const centerOnUser = useCallback((pos, animate = true) => {
  if (!mapRef.current || !pos) return false;
  const { lat, lng } = pos;
  if (!isValidCoordinatePair(lat, lng)) return false;
  const maxZoom = mapRef.current.getMaxZoom?.() ?? 18;
  const zoom = Math.min(maxZoom, USER_LOCATION_LOCATE_ZOOM);
  mapRef.current.setView([lat, lng], zoom, {
    animate,
    duration: animate ? 0.4 : 0,
  });
  return true;
}, [isValidCoordinatePair, mapRef]);

// GPS is a fallback when there are no mappable pins and no restored last view.
// Territory fit wins; do not auto-center on GPS while pins can still load.
useEffect(() => {
  if (loading) return;
  if (restoredLastViewRef?.current) return;
  if (didFitLeadsRef?.current) return;
  if (!currentPosition || hasHandledLocationGrantRef.current) return;
  const { lat, lng } = currentPosition;
  if (!isValidCoordinatePair(lat, lng)) return;

  const validPinCount = (filteredLeads || []).filter((lead) => !!getLeadCoordinates(lead)).length;
  if (validPinCount > 0) return;

  hasHandledLocationGrantRef.current = true;
  suppressAutoFitUntilRef.current = Date.now() + 12000;
  userZoomedRef.current = true;
  centerOnUser(currentPosition, true);
}, [
  loading,
  currentPosition,
  filteredLeads,
  restoredLastViewRef,
  didFitLeadsRef,
  centerOnUser,
  getLeadCoordinates,
  isValidCoordinatePair,
  userZoomedRef,
]);



// ---------------- GLASSY BUTTONS ----------------
  const glassyButtonSx = {
  borderRadius: "10px",
  px: 2,
  py: 0.9,
  textTransform: "none",
  fontWeight: 500,
  fontSize: "13px",

  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(255,255,255,0.75)",

  color: "text.primary",

  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.18)"
      : "rgba(0,0,0,0.12)",

  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",

  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 10px rgba(0,0,0,0.4)"
      : "0 4px 12px rgba(0,0,0,0.12)",

  "&:hover": {
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(255,255,255,0.9)",
  },

  "&:active": {
    transform: "scale(0.97)",
  },
};
// ---------------- GLASSY PANELS/POP UPS ----------------
const glassyPanelSx = {
  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(30, 41, 59, 0.85)" // slate-800 glass
      : "rgba(255,255,255,0.85)",

  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",

  border: "1px solid",
  borderColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)",

  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 30px rgba(0,0,0,0.55)"
      : "0 10px 30px rgba(0,0,0,0.18)",
};

const compactFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    minHeight: 44,
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(15,23,42,0.88)" : "#fff",
    fontSize: "0.95rem",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 2px rgba(0,0,0,0.28)"
        : "0 1px 2px rgba(0,0,0,0.04)",
    "& fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.08)",
    },
    "&:hover fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.14)"
          : "rgba(0,0,0,0.14)",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    padding: "11px 14px",
    fontSize: "0.95rem",
  },
};

const compactMultilineFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(15,23,42,0.88)" : "#fff",
    fontSize: "0.95rem",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 2px rgba(0,0,0,0.28)"
        : "0 1px 2px rgba(0,0,0,0.04)",
    "& fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.08)",
    },
    "&:hover fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.14)"
          : "rgba(0,0,0,0.14)",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-inputMultiline": {
    padding: "10px 14px",
    lineHeight: 1.45,
    fontSize: "0.95rem",
  },
};

const drawerStatLabelSx = {
  fontSize: 12,
  color: "text.secondary",
  mb: 0.35,
};

const drawerStatValueSx = {
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1.2,
};

// Status chip styles: light bg + darker text per status (pill chip)
const STATUS_CHIP_STYLES = {
  "new": { bg: "#E3F2FD", color: "#1976D2" },
  "follow up needed": { bg: "#FFF3E0", color: "#E65100" },
  "appointment scheduled": { bg: "#E8F5E9", color: "#2E7D32" },
  "not home": { bg: "#EEEEEE", color: "#616161" },
  "renter": { bg: "#F3E5F5", color: "#7B1FA2" },
  "interested": { bg: "#E4FFDC", color: "#39FF14" },
  "not interested": { bg: "#FFEBEE", color: "#C62828" },
  "vacant": { bg: "#EFEBE9", color: "#5D4037" },
  "closed won": { bg: "#E8F5E9", color: "#2E7D32" },
  "gated/no access": { bg: "#FFEBEE", color: "#5E0F00" },
  "competitor installed": { bg: "#FFEBEE", color: "#E53935" },
  "adt installed": { bg: "#FFEBEE", color: "#B71C1C" },
};
const getStatusChipStyle = (status) =>
  STATUS_CHIP_STYLES[(status || "New").toLowerCase().trim()] ||
  STATUS_CHIP_STYLES["new"];

  // ---------------- DERIVED VALUES & NEW DOCK STYLES ----------------
  const activeFilterCount =
    (selectedStatusFilters?.size || 0) +
    (soldDateFilter ? 1 : 0) +
    (selectedTerritories?.length > 0 ? 1 : 0);

  const routeModeActive = selectedLeads.length > 0;

  const filterDockButtonSx = {
    borderRadius: "16px",
    px: 2.2,
    py: 1.05,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "0 8px 22px rgba(0,0,0,0.14)",
  };

  const routeDockPaperSx = {
    borderRadius: "22px",
    p: 1.5,
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(15,23,42,0.94)"
        : "rgba(255,255,255,0.94)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 32px rgba(0,0,0,0.32)"
        : "0 12px 32px rgba(0,0,0,0.16)",
  };

  const filterRowButtonSx = {
    justifyContent: "space-between",
    textTransform: "none",
    borderRadius: "16px",
    px: 1.25,
    py: 1.15,
    color: "text.primary",
    bgcolor: "transparent",
    "&:hover": {
      bgcolor: theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.04)",
    },
  };

  const compactDateFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      height: 40,
      background:
        theme.palette.mode === "dark"
          ? "rgba(15,23,42,0.88)"
          : "rgba(255,255,255,0.9)",
    },
    "& .MuiOutlinedInput-input": {
      py: "8px",
      fontSize: "0.9rem",
    },
  };

  const floatingRoundControlSx = {
    width: 42,
    height: 42,
    minWidth: 42,
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.10)"
        : "rgba(255,255,255,0.92)",
    color: "text.primary",
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.14)"
        : "rgba(0,0,0,0.08)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 18px rgba(0,0,0,0.45)"
        : "0 8px 18px rgba(0,0,0,0.14)",
    cursor: "pointer",
    "&:hover": {
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.14)"
          : "rgba(255,255,255,0.98)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
  };

  const floatingPillControlSx = {
    borderRadius: "16px",
    px: 2,
    py: 1,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.92rem",
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.10)"
        : "rgba(255,255,255,0.92)",
    color: "text.primary",
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.14)"
        : "rgba(0,0,0,0.08)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 18px rgba(0,0,0,0.45)"
        : "0 8px 18px rgba(0,0,0,0.14)",
    "&:hover": {
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.14)"
          : "rgba(255,255,255,0.98)",
    },
  };

  const mobileTopInset =
    "var(--topbar-height, calc(64px + env(safe-area-inset-top, 0px)))";

  // Lead drawer expansion (second tap = expand to show edit form) - must be declared before use in useEffect/handleDrawerSave
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [mobileRequiredChoice, setMobileRequiredChoice] = useState(null);
  const [drawerEditData, setDrawerEditData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    status: "",
    notes: "",
    sms_email_opt_in: "",
    followUpDate: "",
  });
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerProposalAction, setDrawerProposalAction] = useState(() =>
    mobileDrawerProposalAction([])
  );
  const [navChooserOpen, setNavChooserOpen] = useState(false);
  const [rememberNavChoice, setRememberNavChoice] = useState(true);
  const [pendingNavDestination, setPendingNavDestination] = useState(null);
  const navProviderOptions = useMemo(() => getProviderOptions(), []);
  const DRAWER_STATUS_OPTIONS = LEAD_STATUS_DEFINITIONS.map((d) => ({
    value: d.value,
    label: d.value,
    icon: d.Icon,
    color: d.color,
  }));

  // Return from a proposal opened on this pin. mapLead is separate from leadId so
  // the desktop "View on Map" handler does not replace the route selection.
  useEffect(() => {
    const mapLead = searchParams.get("mapLead");
    if (!mapLead || mapLeadRestoredRef.current === mapLead) return;

    let cancelled = false;
    (async () => {
      const pool = [...(filteredLeads || []), ...(leads || [])];
      let lead = pool.find((item) => String(item.id) === String(mapLead));
      if (!lead) {
        try {
          const res = await API.get(`/leads/${mapLead}/`);
          lead = res.data;
        } catch {
          return;
        }
      }
      if (cancelled || !lead) return;
      mapLeadRestoredRef.current = mapLead;
      setSelectedLeadForDrawer(lead);
      const next = new URLSearchParams(searchParams);
      next.delete("mapLead");
      setSearchParams(next, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, filteredLeads, leads, setSearchParams, setSelectedLeadForDrawer]);

  const openProposalFromDrawer = () => {
    const leadId = selectedLeadForDrawer?.id;
    if (!leadId) return;
    navigateToLeadDetail(navigate, leadId, {
      pathname: "/leads",
      search: `?mapLead=${leadId}`,
      label: "Map",
    });
  };

  // Mobile drawer: seed from map pin, then always load full lead from GET /leads/:id/
  useEffect(() => {
    if (!selectedLeadForDrawer) return;

    const leadId = selectedLeadForDrawer.id;
    const seed = selectedLeadForDrawer;

    const applyDrawerFromLead = (lead) => {
      const incompleteFromList = (lead.followups || []).find((f) => !f.completed);
      const followUpDateFromList = incompleteFromList?.due_date
        ? new Date(incompleteFromList.due_date).toISOString().split("T")[0]
        : "";
      setDrawerEditData({
        first_name: lead.first_name || "",
        last_name: lead.last_name || "",
        email: lead.email || "",
        phone: formatPhone(lead.phone),
        status: lead.status || "New",
        notes: lead.notes || "",
        sms_email_opt_in: lead.sms_email_opt_in ?? "",
        followUpDate: followUpDateFromList,
      });
    };

    applyDrawerFromLead(seed);
    setDrawerProposalAction(mobileDrawerProposalAction([]));
    setDrawerExpanded(false);
    setDrawerDragging(false);
    setDrawerDragUiMode(null);
    leadDrawerDragUiModeRef.current = null;

    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(`/leads/${leadId}/`);
        if (cancelled) return;
        const full = res.data;
        setSelectedLeadForDrawer(full);
        applyDrawerFromLead(full);
        try {
          const proposalsRes = await API.get(`/leads/${leadId}/proposals/`);
          if (!cancelled) {
            setDrawerProposalAction(mobileDrawerProposalAction(proposalsRes.data));
          }
        } catch {
          /* keep the create-proposal button if proposal history cannot load */
        }
      } catch (_) {
        /* keep map-seeded drawer if detail fails */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedLeadForDrawer?.id, setSelectedLeadForDrawer]);

  const handleDrawerSave = useCallback(async () => {
    if (!selectedLeadForDrawer) return;
    if ((drawerEditData.phone || "").trim() && !drawerEditData.sms_email_opt_in) {
      setMobileRequiredChoice("opt-in");
      return;
    }
    if (drawerEditData.status === "Follow Up Needed" && !drawerEditData.followUpDate) {
      setMobileRequiredChoice("follow-up");
      return;
    }
    setDrawerSaving(true);
    try {
      const payload = {
        first_name: (drawerEditData.first_name || "").trim(),
        last_name: (drawerEditData.last_name || "").trim(),
        email: (drawerEditData.email || "").trim() || null,
        phone: formatPhone(drawerEditData.phone) || null,
        status: drawerEditData.status || "New",
        notes: drawerEditData.notes || "",
        sms_email_opt_in: drawerEditData.sms_email_opt_in || null,
      };
      const res = await API.patch(`/leads/${selectedLeadForDrawer.id}/`, payload);
      const leadId = selectedLeadForDrawer.id;
      let finalLead = res.data;

      if (drawerEditData.status === "Follow Up Needed" && drawerEditData.followUpDate) {
        try {
          const followupsFromResponse = res.data.followups || [];
          const incompleteFollowUp = followupsFromResponse.find((f) => !f.completed);

          if (incompleteFollowUp) {
            const isInitialSchedule = !incompleteFollowUp.due_date;
            await API.patch(`/followups/${incompleteFollowUp.id}/`, {
              due_date: drawerEditData.followUpDate,
              notes: isInitialSchedule
                ? `Follow-up scheduled for ${drawerEditData.followUpDate}`
                : `Follow-up rescheduled to ${drawerEditData.followUpDate}`,
            });
            toast.success("Lead and follow-up updated");
          } else {
            await API.post("/followups/", {
              lead: leadId,
              due_date: drawerEditData.followUpDate,
              completed: false,
              notes: `Follow-up scheduled for ${drawerEditData.followUpDate}`,
            });
            toast.success("Lead updated; follow-up created");
          }
          try {
            const refreshed = await API.get(`/leads/${leadId}/`);
            finalLead = refreshed.data;
          } catch (_) {
            /* keep PATCH response */
          }
        } catch (followUpErr) {
          console.error("Mobile drawer follow-up save failed:", followUpErr);
          toast.warning("Lead updated but follow-up could not be saved");
        }
      } else {
        toast.success("Lead updated");
      }

      emitLeadUpdate(finalLead);
      setSelectedLeadForDrawer(null);
      setDrawerExpanded(false);
    } catch (err) {
      const data = err.response?.data;
      let msg = "Failed to update lead";
      if (data) {
        if (typeof data.detail === "string") msg = data.detail;
        else if (Array.isArray(data.detail)) msg = data.detail[0]?.msg || data.detail[0] || msg;
        else if (typeof data === "object") {
          const first = Object.entries(data).find(([, v]) => Array.isArray(v) && v[0]);
          if (first) msg = first[1][0];
        }
      }
      console.error("Drawer save failed:", data || err);
      toast.error(msg);
    } finally {
      setDrawerSaving(false);
    }
  }, [selectedLeadForDrawer, drawerEditData, setSelectedLeadForDrawer]);

  const LEAD_DRAWER_DRAG_THRESHOLD_PX = 40;
  const LEAD_DRAWER_DRAG_VELOCITY = 0.35;
  const LEAD_DRAWER_SNAP_MS = 520;
  const LEAD_DRAWER_SNAP_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
  const leadDrawerSnapTransition = `height ${LEAD_DRAWER_SNAP_MS}ms ${LEAD_DRAWER_SNAP_EASING}, max-height ${LEAD_DRAWER_SNAP_MS}ms ${LEAD_DRAWER_SNAP_EASING}`;

  const [leadDrawerViewportH, setLeadDrawerViewportH] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const [drawerDragging, setDrawerDragging] = useState(false);
  const [drawerDragUiMode, setDrawerDragUiMode] = useState(null);

  const leadDrawerDragRef = useRef({
    startY: 0,
    startTime: 0,
    startHeightPx: 0,
    liveHeightPx: 0,
    tracking: false,
  });
  const leadDrawerDragZoneRef = useRef(null);
  const leadDrawerPaperRef = useRef(null);
  const leadDrawerColumnRef = useRef(null);
  const leadDrawerDragFrameRef = useRef(null);
  const leadDrawerPendingClientYRef = useRef(null);
  const leadDrawerDragUiModeRef = useRef(null);
  const leadDrawerSnapRef = useRef({ compactPx: 400, expandedPx: 700 });

  useEffect(() => {
    const update = () => setLeadDrawerViewportH(window.innerHeight);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const leadDrawerCompactPx = useMemo(
    () => Math.round(leadDrawerViewportH * 0.55),
    [leadDrawerViewportH]
  );
  const leadDrawerExpandedPx = useMemo(
    () => Math.round(leadDrawerViewportH * 0.88),
    [leadDrawerViewportH]
  );

  useEffect(() => {
    leadDrawerSnapRef.current = {
      compactPx: leadDrawerCompactPx,
      expandedPx: leadDrawerExpandedPx,
    };
  }, [leadDrawerCompactPx, leadDrawerExpandedPx]);

  const leadDrawerRestingHeightPx = drawerExpanded
    ? leadDrawerExpandedPx
    : leadDrawerCompactPx;
  const leadDrawerMidPx = (leadDrawerCompactPx + leadDrawerExpandedPx) / 2;

  const showLeadDrawerCompactFooter = drawerDragging
    ? drawerDragUiMode === "compact"
    : !drawerExpanded;
  const showLeadDrawerExpandedUi = drawerDragging
    ? drawerDragUiMode === "expanded"
    : drawerExpanded;

  const clearLeadDrawerPaperInlineStyles = useCallback(() => {
    const paper = leadDrawerPaperRef.current;
    const col = leadDrawerColumnRef.current;
    if (paper) {
      paper.style.height = "";
      paper.style.maxHeight = "";
      paper.style.transition = "";
      paper.style.willChange = "";
      paper.style.contain = "";
      paper.style.transform = "";
    }
    if (col) {
      col.style.maxHeight = "";
      col.style.transition = "";
    }
  }, []);

  const syncLeadDrawerPaperHeightDom = useCallback((heightPx, dragging) => {
    const px = `${Math.round(heightPx)}px`;
    const paper = leadDrawerPaperRef.current;
    const col = leadDrawerColumnRef.current;
    if (paper) {
      paper.style.height = px;
      paper.style.maxHeight = px;
      if (dragging) {
        paper.style.transition = "none";
        paper.style.willChange = "height";
        paper.style.contain = "layout style";
        paper.style.transform = "translateZ(0)";
      } else {
        paper.style.transform = "";
      }
    }
    if (col) {
      col.style.maxHeight = px;
      if (dragging) col.style.transition = "none";
    }
  }, []);

  const maybeUpdateDragUiMode = useCallback((heightPx) => {
    const { compactPx, expandedPx } = leadDrawerSnapRef.current;
    const mid = (compactPx + expandedPx) / 2;
    const hysteresisPx = 18;
    const prev = leadDrawerDragUiModeRef.current;
    let next = prev;
    if (heightPx >= mid + hysteresisPx) next = "expanded";
    else if (heightPx <= mid - hysteresisPx) next = "compact";
    if (next && next !== prev) {
      leadDrawerDragUiModeRef.current = next;
      setDrawerDragUiMode(next);
    }
  }, []);

  const scheduleLeadDrawerDragMove = useCallback(
    (clientY) => {
      leadDrawerPendingClientYRef.current = clientY;
      if (leadDrawerDragFrameRef.current) return;
      leadDrawerDragFrameRef.current = requestAnimationFrame(() => {
        leadDrawerDragFrameRef.current = null;
        const y = leadDrawerPendingClientYRef.current;
        if (y == null) return;

        const ref = leadDrawerDragRef.current;
        if (!ref.tracking) return;

        const { compactPx, expandedPx } = leadDrawerSnapRef.current;
        const deltaY = y - ref.startY;
        const nextH = Math.min(
          expandedPx,
          Math.max(0, ref.startHeightPx - deltaY)
        );
        ref.liveHeightPx = nextH;
        syncLeadDrawerPaperHeightDom(nextH, true);
        maybeUpdateDragUiMode(nextH);
      });
    },
    [syncLeadDrawerPaperHeightDom, maybeUpdateDragUiMode]
  );

  const cancelLeadDrawerDragFrame = useCallback(() => {
    if (leadDrawerDragFrameRef.current) {
      cancelAnimationFrame(leadDrawerDragFrameRef.current);
      leadDrawerDragFrameRef.current = null;
    }
    leadDrawerPendingClientYRef.current = null;
  }, []);

  const closeLeadDrawer = useCallback(() => {
    cancelLeadDrawerDragFrame();
    setDrawerDragUiMode(null);
    leadDrawerDragUiModeRef.current = null;
    setDrawerDragging(false);
    setDrawerExpanded(false);
    setSelectedLeadForDrawer(null);
    requestAnimationFrame(() => {
      clearLeadDrawerPaperInlineStyles();
    });
  }, [setSelectedLeadForDrawer, cancelLeadDrawerDragFrame, clearLeadDrawerPaperInlineStyles]);

  const beginLeadDrawerDrag = useCallback(
    (clientY) => {
      const { compactPx, expandedPx } = leadDrawerSnapRef.current;
      const startHeightPx = drawerExpanded ? expandedPx : compactPx;
      const uiMode = drawerExpanded ? "expanded" : "compact";
      leadDrawerDragRef.current = {
        startY: clientY,
        startTime: Date.now(),
        startHeightPx,
        liveHeightPx: startHeightPx,
        tracking: true,
      };
      leadDrawerDragUiModeRef.current = uiMode;
      setDrawerDragUiMode(uiMode);
      setDrawerDragging(true);
      syncLeadDrawerPaperHeightDom(startHeightPx, true);
    },
    [drawerExpanded, syncLeadDrawerPaperHeightDom]
  );

  const finishLeadDrawerDrag = useCallback(
    (clientY) => {
      const ref = leadDrawerDragRef.current;
      if (!ref.tracking) return;
      ref.tracking = false;
      cancelLeadDrawerDragFrame();

      const { compactPx, expandedPx } = leadDrawerSnapRef.current;
      const deltaY = clientY - ref.startY;
      const elapsed = Math.max(Date.now() - ref.startTime, 1);
      const velocity = deltaY / elapsed;
      const releaseHeight =
        ref.liveHeightPx ??
        Math.min(expandedPx, Math.max(0, ref.startHeightPx - deltaY));
      const midPx = (compactPx + expandedPx) / 2;
      const dismissThreshold = compactPx * 0.38;

      const swipeUp =
        deltaY < -LEAD_DRAWER_DRAG_THRESHOLD_PX ||
        velocity < -LEAD_DRAWER_DRAG_VELOCITY;
      const swipeDown =
        deltaY > LEAD_DRAWER_DRAG_THRESHOLD_PX ||
        velocity > LEAD_DRAWER_DRAG_VELOCITY;

      const dismiss =
        releaseHeight < dismissThreshold ||
        (swipeDown &&
          releaseHeight < compactPx * 0.45 &&
          velocity > LEAD_DRAWER_DRAG_VELOCITY);

      if (dismiss) {
        closeLeadDrawer();
        return;
      }

      clearLeadDrawerPaperInlineStyles();
      setDrawerDragging(false);
      setDrawerDragUiMode(null);
      leadDrawerDragUiModeRef.current = null;

      if (releaseHeight >= midPx || swipeUp) {
        setDrawerExpanded(true);
        return;
      }
      setDrawerExpanded(false);
    },
    [cancelLeadDrawerDragFrame, clearLeadDrawerPaperInlineStyles, closeLeadDrawer]
  );

  const onLeadDrawerDragTouchStart = useCallback(
    (e) => {
      const touch = e.touches?.[0];
      if (touch) beginLeadDrawerDrag(touch.clientY);
    },
    [beginLeadDrawerDrag]
  );

  const onLeadDrawerDragTouchEnd = useCallback(
    (e) => {
      const touch = e.changedTouches?.[0];
      if (touch) finishLeadDrawerDrag(touch.clientY);
    },
    [finishLeadDrawerDrag]
  );

  const onLeadDrawerDragPointerDown = useCallback(
    (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      beginLeadDrawerDrag(e.clientY);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [beginLeadDrawerDrag]
  );

  const onLeadDrawerDragPointerUp = useCallback(
    (e) => {
      if (!leadDrawerDragRef.current.tracking) return;
      finishLeadDrawerDrag(e.clientY);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
    },
    [finishLeadDrawerDrag]
  );

  useEffect(() => {
    if (!drawerDragging) return undefined;

    const onTouchMove = (e) => {
      if (!leadDrawerDragRef.current.tracking) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      if (Math.abs(touch.clientY - leadDrawerDragRef.current.startY) > 6) {
        e.preventDefault();
      }
      scheduleLeadDrawerDragMove(touch.clientY);
    };

    const onPointerMove = (e) => {
      if (!leadDrawerDragRef.current.tracking) return;
      if (e.pointerType === "touch") return;
      scheduleLeadDrawerDragMove(e.clientY);
    };

    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("pointermove", onPointerMove);
      cancelLeadDrawerDragFrame();
    };
  }, [drawerDragging, scheduleLeadDrawerDragMove, cancelLeadDrawerDragFrame]);

  const handleNavigateLead = useCallback(
    (lead) => {
      const destination = buildNavigationDestination(lead);
      if (!destination.address && (destination.lat == null || destination.lng == null)) {
        toast.error("No address available for navigation");
        return;
      }

      const savedProvider = getSavedNavProvider();
      if (savedProvider) {
        openNavigation({ provider: savedProvider, destination });
        return;
      }

      setPendingNavDestination(destination);
      setSelectedLeadForDrawer(null);
      setDrawerExpanded(false);
      setNavChooserOpen(true);
    },
    [setSelectedLeadForDrawer]
  );

  const handleChooseNavProvider = useCallback(
    (provider) => {
      if (!pendingNavDestination) return;
      if (rememberNavChoice) saveNavProvider(provider);
      openNavigation({ provider, destination: pendingNavDestination });
      setNavChooserOpen(false);
      setPendingNavDestination(null);
    },
    [pendingNavDestination, rememberNavChoice]
  );

  const handleNavChooserClose = useCallback(() => {
    setNavChooserOpen(false);
    setPendingNavDestination(null);
  }, []);

  const [locating, setLocating] = useState(false);
  
  // Territory filter state (mobile only)
  const [territoryFilterOpen, setTerritoryFilterOpen] = useState(false);

  // Unified filters sheet state
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [showStatusSection, setShowStatusSection] = useState(true);
  const [showDateSection, setShowDateSection] = useState(false);
  const [showTerritorySection, setShowTerritorySection] = useState(false);
  const [showCustomDateFields, setShowCustomDateFields] = useState(false);

  // ================= DISABLE LEAFLET INTERACTIONS WHEN BOX SELECT IS ACTIVE (MOBILE SAFE) =================
useEffect(() => {
  const mapContainer = document.querySelector(".leaflet-container");
  if (!mapContainer || !mapRef.current) return;

  const map = mapRef.current;

  if (boxSelectMode) {
    // Disable Leaflet interactions
    map.dragging.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.scrollWheelZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();

    // Disable browser native gestures (CRITICAL)
    mapContainer.style.touchAction = "none";
    mapContainer.style.webkitUserSelect = "none";
    mapContainer.style.userSelect = "none";
    mapContainer.style.webkitTouchCallout = "none"; // 🔥 disables iOS magnifier
    mapContainer.style.webkitTapHighlightColor = "transparent";
  } else {
    // Re-enable Leaflet interactions
    map.dragging.enable();
    map.touchZoom.enable();
    map.doubleClickZoom.enable();
    map.scrollWheelZoom.enable();
    map.boxZoom.enable();
    map.keyboard.enable();

    // Restore browser defaults
    mapContainer.style.touchAction = "auto";
    mapContainer.style.webkitUserSelect = "auto";
    mapContainer.style.userSelect = "auto";
    mapContainer.style.webkitTouchCallout = "default";
    mapContainer.style.webkitTapHighlightColor = "initial";
  }
}, [boxSelectMode]);


  
  // Wrapper for clear route that also closes the lead drawer
  const handleClearRouteWithDrawer = useCallback(() => {
    handleClearRoute();
    setSelectedLeadForDrawer(null);
  }, [handleClearRoute]);

  // Handle zoom to user's current location
 const handleZoomToMyLocation = useCallback(async () => {
  if (!mapRef.current || zoomingToLocationRef.current || locating) return;
  if (!navigator?.geolocation) return;

  const isValidPosition = (pos) => {
    if (!pos) return false;
    const { lat, lng, accuracy } = pos;
    return (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180 &&
      Number.isFinite(accuracy)
    );
  };

  const runSetView = (pos) => {
    // Lock out auto-fit briefly so filters/territories cannot immediately override explicit intent.
    suppressAutoFitUntilRef.current = Date.now() + 12000;
    userZoomedRef.current = true;
    centerOnUser(pos, true);
  };

  zoomingToLocationRef.current = true;
  setLocating(true);
  try {
    const fresh = await Promise.race([
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp,
            }),
          reject,
          { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
        );
      }),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("location-timeout")), 13000);
      }),
    ]);

    if (isValidPosition(fresh)) {
      lastKnownGoodPositionRef.current = fresh;
      runSetView(fresh);
      return;
    }
  } catch (_) {
    // Fallback handled below.
  } finally {
    zoomingToLocationRef.current = false;
    setLocating(false);
  }

  const fallback = lastKnownGoodPositionRef.current;
  if (isValidPosition(fallback)) {
    const ageMs = Number.isFinite(fallback.timestamp) ? Date.now() - fallback.timestamp : Infinity;
    if (ageMs <= 30000) {
      runSetView(fallback);
    }
  }
}, [locating, mapRef, userZoomedRef, centerOnUser]);




  return (
    <Box sx={{ width: "100%", height: "100dvh", overflow: "hidden" }}>

      {/* ================= FULLSCREEN MAP ================= */}
      <Box
        sx={{
          position: "fixed",
          top: mobileTopInset,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
        <MapContainer
          key={`leads-mobile-map-${mapLayoutKey}-${
            mobileClusterActive
              ? `cluster-${isPhoneWidth ? "phone" : "tablet"}`
              : "plain"
          }`}
          center={initialMapCenter}
          zoom={initialMapZoom}
          ref={mapRef}
          zoomAnimation={false}
          markerZoomAnimation={false}
          fadeAnimation={false}
          dragging={!boxSelectMode}
          style={{ height: "100%", width: "100%" }}
        >
  {/* Add zoom listener as a separate component */}
  <MapZoomListener 
    userZoomedRef={userZoomedRef} 
    programmaticZoomRef={programmaticZoomRef}
    viewportFetchEnabled={viewportFetchEnabled}
  />

          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {viewportFetchEnabled && applyMapData && setLoading && (
            <MapViewportFetcher
              territoryId={territoryIdForViewport}
              refreshTick={viewportRefreshTick}
              onApply={applyMapData}
              setLoading={setLoading}
            />
          )}

          <MapLastViewPersistence
            enabled={persistLeadsMapLastView}
            userId={persistLeadsMapUserId}
          />

          <FitBoundsOnData
            leads={filteredLeads}
            userZoomedRef={userZoomedRef}
            initialFitRef={initialFitRef}
            fitModeRef={fitModeRef}
            suppressAutoFitUntilRef={suppressAutoFitUntilRef}
            didFitLeadsRef={didFitLeadsRef}
          />
          <MapHqFallback
            loading={loading}
            leads={filteredLeads}
            companyHqView={companyHqView}
            restoredLastViewRef={restoredLastViewRef}
            didFitLeadsRef={didFitLeadsRef}
            userZoomedRef={userZoomedRef}
            initialFitRef={initialFitRef}
            gpsResolved={
              startupGpsResolved ||
              Boolean(currentPosition) ||
              Boolean(geoError) ||
              (typeof navigator !== "undefined" && !navigator.geolocation)
            }
          />
          <CenterOnSelectedLead
            selectedLeads={selectedLeads}
            userZoomedRef={userZoomedRef}
            programmaticZoomRef={programmaticZoomRef}
            focusLead={viewOnMapLead}
            zoomLevel={viewLeadZoom}
            onZoomApplied={onViewLeadZoomApplied}
          />
          <MapClickHandler
            onMapClick={() => {
              // Close filters sheet when map is clicked
              if (filtersSheetOpen) {
                setFiltersSheetOpen(false);
              }
            }}
          />
          {mobileClusterActive ? (
            <ClusteredLeadMarkers
              leads={filteredLeads}
              selectedLeads={selectedLeads}
              handleMarkerClick={handleMarkerClick}
              drawerLeadId={selectedLeadForDrawer?.id}
              highlightedLeadId={highlightedLeadId}
              clusterOptions={mobileClusterOptions}
            />
          ) : (
            filteredLeads
              .filter((lead) => {
                const lat = lead.latitude || lead.lat;
                const lng = lead.longitude || lead.lng;
                return lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
              })
              .map((lead) => {
                const isSelected = selectedLeads.some((l) => l.id === lead.id);
                const isDrawerSelected =
                  !isSelected &&
                  (selectedLeadForDrawer?.id === lead.id || highlightedLeadId === lead.id);
                const isDisabled = lead.in_other_rep_route;

                return (
                  <DynamicMarker
                    key={lead.id}
                    lead={lead}
                    isSelected={isSelected}
                    isDrawerSelected={isDrawerSelected}
                    isDisabled={isDisabled}
                    handleMarkerClick={handleMarkerClick}
                  />
                );
              })
          )}

{currentPosition && (
  <UserLocationMapLayers
    position={currentPosition}
    minZoom={userLocationMinZoom}
    icon={MOBILE_USER_LOCATION_ICON}
    accuracyRadius={currentPosition.accuracy}
    accuracyPathOptions={USER_LOCATION_ACCURACY_PATH_OPTIONS}
  />
)}


          <BoxSelectHandler
            enabled={boxSelectMode}
            onStart={handleBoxSelectStart}
            onMove={handleBoxSelectMove}
            onEnd={handleBoxSelectEnd}
          />

          {selectionBox && <SelectionBoxOverlay bounds={selectionBox} />}
        </MapContainer>
      </Box>

      {/* ================= ZOOM TO MY LOCATION BUTTON ================= */}
      <Box
        sx={{
          position: "fixed",
          top: `calc(${mobileTopInset} + 96px)`,
          left: 12,
          zIndex: 3000,
          ...floatingRoundControlSx,
          opacity: locating ? 0.65 : 1,
        }}
        onClick={handleZoomToMyLocation}
      >
        {locating ? (
          <CircularProgress size={18} />
        ) : (
          <MyLocationIcon sx={{ fontSize: 20 }} />
        )}
      </Box>

      {/* ================= MY SAVED ROUTES (phone width) ================= */}
      {isPhoneWidth && (
      <Box
        sx={{
          position: "fixed",
          top: `calc(${mobileTopInset} + 20px)`,
          right: 12,
          zIndex: 3000,
        }}
      >
        <Button
          size="small"
          startIcon={<PushPinIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate("/leadmap/savedroutes")}
          sx={floatingPillControlSx}
        >
          My Saved Routes
        </Button>
      </Box>
      )}

      {/* ================= UNIFIED MOBILE FILTER / ROUTE CONTROLS ================= */}

      {!routeModeActive && (
        <Box
          sx={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "calc(18px + env(safe-area-inset-bottom, 0px))",
            zIndex: 3000,
            maxWidth: 430,
            mx: "auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            data-tour="mobile-map-filters"
            variant="contained"
            startIcon={<FilterAltOutlinedIcon />}
            onClick={() => {
              setFiltersSheetOpen(true);
              setShowCustomDateFields(soldDateFilter === "custom");
            }}
            sx={filterDockButtonSx}
          >
            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
          </Button>
        </Box>
      )}

      {routeModeActive && (
        <Box
          sx={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "calc(18px + env(safe-area-inset-bottom, 0px))",
            zIndex: 3000,
            maxWidth: 430,
            mx: "auto",
          }}
        >
          <Paper elevation={0} sx={routeDockPaperSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 1.2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.2px",
                  }}
                >
                  Route Builder
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                >
                  {selectedLeads.length} stop{selectedLeads.length === 1 ? "" : "s"} selected
                </Typography>
              </Box>

              <Chip
                icon={<RouteIcon sx={{ fontSize: 16 }} />}
                label={selectedLeads.length}
                size="small"
                sx={{
                  height: 28,
                  borderRadius: "999px",
                  fontWeight: 700,
                  bgcolor: "rgba(25,118,210,0.10)",
                  color: "#1565C0",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSaveRouteClick}
              disabled={selectedLeads.length === 0}
              sx={{
                borderRadius: "16px",
                py: 1.2,
                mb: 1.1,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.98rem",
                boxShadow: "0 8px 20px rgba(25,118,210,0.25)",
              }}
            >
              Save Route
            </Button>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearRouteWithDrawer}
                sx={{
                  borderRadius: "16px",
                  py: 1.05,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderColor: "rgba(0,0,0,0.12)",
                  bgcolor: theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "#fff",
                }}
              >
                Clear
              </Button>

              <Button
                fullWidth
                variant={boxSelectMode ? "contained" : "outlined"}
                startIcon={<FilterAltOutlinedIcon />}
                onClick={toggleBoxSelect}
                sx={{
                  borderRadius: "16px",
                  py: 1.05,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderColor: boxSelectMode ? "transparent" : "rgba(0,0,0,0.12)",
                  bgcolor: boxSelectMode
                    ? theme.palette.primary.main
                    : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fff"),
                  color: boxSelectMode ? "#fff" : "text.primary",
                }}
              >
                Box Select
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Drawer
        anchor="bottom"
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(15,23,42,0.98)"
                : "rgba(255,255,255,0.98)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 -10px 30px rgba(0,0,0,0.45)"
                : "0 -10px 30px rgba(0,0,0,0.14)",
            borderTop: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
          },
        }}
        sx={{ zIndex: 3500 }}
      >
        <Box sx={{ p: 2.2, pt: 1.15, pb: 2.3 }}>
          <Box
            sx={{
              width: 42,
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(120,120,120,0.35)",
              mx: "auto",
              mb: 1.5,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 1.3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                Filters
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.secondary",
                  mt: 0.35,
                }}
              >
                Narrow leads on the map
              </Typography>
            </Box>

            <IconButton size="small" onClick={() => setFiltersSheetOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Stack spacing={0.35}>
            <Button
              fullWidth
              variant="text"
              onClick={() => setShowStatusSection((prev) => !prev)}
              sx={filterRowButtonSx}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                <TuneIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                    Lead Status
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                    {selectedStatusFilters.size > 0
                      ? `${selectedStatusFilters.size} active filter${selectedStatusFilters.size > 1 ? "s" : ""}`
                      : "No status filters applied"}
                  </Typography>
                </Box>
              </Box>

              {selectedStatusFilters.size > 0 && (
                <Chip
                  size="small"
                  label={selectedStatusFilters.size}
                  sx={{
                    height: 22,
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    borderRadius: "999px",
                    bgcolor: "primary.main",
                    color: "#fff",
                  }}
                />
              )}
            </Button>

            {showStatusSection && (
              <Box
                sx={{
                  mt: 0.35,
                  p: 1.35,
                  borderRadius: "16px",
                  ...glassyPanelSx,
                }}
              >
                {selectedStatusFilters.size > 0 && (
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                      {selectedStatusFilters.size} filter(s) active
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleMobileClearAll}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.25,
                        fontSize: 10,
                        textTransform: "none",
                        color: "primary.main",
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.6,
                  }}
                >
                  {Object.entries(STATUS_COLORS).map(([label, color]) => {
                    const statusKey = label.toLowerCase();
                    const isSelected = isStatusSelected(statusKey);

                    return (
                      <Box
                        key={label}
                        onClick={() => handleStatusFilterToggle(statusKey)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          px: 0.25,
                          py: 0.15,
                          borderRadius: 1,
                          bgcolor: isSelected ? "action.selected" : "transparent",
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: color,
                            border: isSelected ? "2px solid" : "none",
                            borderColor: isSelected ? "primary.main" : "transparent",
                            boxShadow: isSelected ? "0 0 0 2px rgba(25,118,210,0.2)" : "none",
                          }}
                        />
                        <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
                      </Box>
                    );
                  })}

                  {[
                    ["new-adt", "#F5D327", "New (ADT)"],
                    ["new-title-realtor", "#A627F5", "New (Title Co)"],
                    ["new-builder", "#F527C8", "New (Builder)"],
                  ].map(([key, color, label]) => {
                    const isSelected = isStatusSelected(key);
                    return (
                      <Box
                        key={key}
                        onClick={() => handleStatusFilterToggle(key)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          px: 0.25,
                          py: 0.15,
                          borderRadius: 1,
                          bgcolor: isSelected ? "action.selected" : "transparent",
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: color,
                            border: isSelected ? "2px solid" : "none",
                            borderColor: isSelected ? "primary.main" : "transparent",
                            boxShadow: isSelected ? "0 0 0 2px rgba(25,118,210,0.2)" : "none",
                          }}
                        />
                        <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Button
              fullWidth
              variant="text"
              onClick={() => setShowDateSection((prev) => !prev)}
              sx={filterRowButtonSx}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                <CalendarTodayIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                    Lead Date
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                    {soldDateFilter
                      ? (soldDateFilter === "custom"
                          ? "Custom date range"
                          : formatLeadDateLabel(soldDateFilter))
                      : "No date filter applied"}
                  </Typography>
                </Box>
              </Box>
            </Button>

            {showDateSection && (
              <Box
                sx={{
                  mt: 0.35,
                  p: 1.35,
                  borderRadius: "16px",
                  ...glassyPanelSx,
                }}
              >
                <Stack spacing={0.7}>
                  {lastThreeUploadDates.map((dateStr) => (
                    <Button
                      key={`lead-date-inline-${dateStr}`}
                      fullWidth
                      variant={soldDateFilter === dateStr ? "contained" : "text"}
                      onClick={() => {
                        setSoldDateFilter && setSoldDateFilter(dateStr);
                        setSoldDateRange && setSoldDateRange({ start: null, end: null });
                        setShowCustomDateFields(false);
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        borderRadius: "12px",
                      }}
                    >
                      {formatLeadDateLabel(dateStr)}
                    </Button>
                  ))}

                  <Button
                    fullWidth
                    variant={soldDateFilter === "custom" ? "contained" : "text"}
                    onClick={() => {
                      setSoldDateFilter && setSoldDateFilter("custom");
                      setShowCustomDateFields((prev) => !prev || soldDateFilter !== "custom");
                    }}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      borderRadius: "12px",
                    }}
                  >
                    Custom Date Range
                  </Button>

                  {showCustomDateFields && (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                      <TextField
                        type="date"
                        label="Start Date"
                        fullWidth
                        size="small"
                        value={soldDateRange?.start || ""}
                        onChange={(e) =>
                          setSoldDateRange && setSoldDateRange((prev) => ({ ...prev, start: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={compactDateFieldSx}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        fullWidth
                        size="small"
                        value={soldDateRange?.end || ""}
                        onChange={(e) =>
                          setSoldDateRange && setSoldDateRange((prev) => ({ ...prev, end: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={compactDateFieldSx}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {role && ["Admin", "Sales Director", "Manager"].includes(role) && (
              <>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setShowTerritorySection((prev) => !prev)}
                  sx={filterRowButtonSx}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                    <MapIcon sx={{ fontSize: 18, color: "primary.main" }} />
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                        Territories
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.25, mt: 0.2 }}>
                        {selectedTerritories.length > 0
                          ? `${selectedTerritories.length} territor${selectedTerritories.length === 1 ? "y" : "ies"} selected`
                          : "No territory filter applied"}
                      </Typography>
                    </Box>
                  </Box>

                  {selectedTerritories.length > 0 && (
                    <Chip
                      size="small"
                      label={selectedTerritories.length}
                      sx={{
                        height: 22,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        borderRadius: "999px",
                        bgcolor: "primary.main",
                        color: "#fff",
                      }}
                    />
                  )}
                </Button>

                {showTerritorySection && (
                  <Box
                    sx={{
                      mt: 0.35,
                      p: 1.35,
                      borderRadius: "16px",
                      ...glassyPanelSx,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {selectedTerritories.length > 0 && (
                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                          {selectedTerritories.length} territory(ies) selected
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setSelectedTerritories && setSelectedTerritories([])}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.25,
                            fontSize: 10,
                            textTransform: "none",
                            color: "primary.main",
                          }}
                        >
                          Clear All
                        </Button>
                      </Box>
                    )}

                    {territories.length === 0 ? (
                      <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary", py: 1 }}>
                        No territories available
                      </Typography>
                    ) : (
                      territories.map((territory) => {
                        const isSelected = selectedTerritories.includes(territory.id);

                        return (
                          <Box
                            key={territory.id}
                            onClick={() => {
                              if (setSelectedTerritories) {
                                if (isSelected) {
                                  setSelectedTerritories((prev) => prev.filter((id) => id !== territory.id));
                                } else {
                                  setSelectedTerritories((prev) => [...prev, territory.id]);
                                }
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              px: 1,
                              py: 0.75,
                              borderRadius: 1,
                              bgcolor: isSelected ? "action.selected" : "transparent",
                              "&:hover": { bgcolor: "action.hover" },
                              transition: "background-color 0.2s",
                              mb: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "2px",
                                bgcolor: isSelected ? "primary.main" : "grey.300",
                                border: isSelected ? "2px solid" : "1px solid",
                                borderColor: isSelected ? "primary.dark" : "grey.400",
                              }}
                            />
                            <Typography sx={{ fontSize: "13px" }}>{territory.name}</Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>

          <Divider sx={{ my: 1.4 }} />

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                handleMobileClearAll();
                setSoldDateFilter && setSoldDateFilter(null);
                setSoldDateRange && setSoldDateRange({ start: null, end: null });
                setSelectedTerritories && setSelectedTerritories([]);
                setShowCustomDateFields(false);
              }}
              sx={{
                borderRadius: "14px",
                py: 1,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Clear All
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() => setFiltersSheetOpen(false)}
              sx={{
                borderRadius: "14px",
                py: 1,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* ================= LEAD DETAIL DRAWER ================= */}
      {selectedLeadForDrawer && (
         <Drawer
          anchor="bottom"
          open={!!selectedLeadForDrawer}
          onClose={closeLeadDrawer}
          PaperProps={{
            ref: leadDrawerPaperRef,
            "data-tour": "mobile-lead-drawer",
            sx: {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              ...(drawerDragging
                ? {}
                : {
                    maxHeight: `${leadDrawerRestingHeightPx}px`,
                    height: `${leadDrawerRestingHeightPx}px`,
                  }),
              transition: drawerDragging ? "none" : leadDrawerSnapTransition,
              bottom: 0,
              pb: "env(safe-area-inset-bottom, 0px)",
              top: "auto",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(15,23,42,0.96)"
                  : "rgba(255,255,255,0.98)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 -10px 30px rgba(0,0,0,0.45)"
                  : "0 -10px 30px rgba(0,0,0,0.14)",
              borderTop: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
            },
          }}
          sx={{
            zIndex: 4000,
            "& .MuiDrawer-paper": {
              zIndex: 4000,
            },
          }}
        >


          
          <Box
            ref={leadDrawerColumnRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              ...(drawerDragging
                ? {}
                : { maxHeight: `${leadDrawerRestingHeightPx}px` }),
              overflow: "hidden",
              transition: drawerDragging ? "none" : leadDrawerSnapTransition,
            }}
          >
            <Box
              ref={leadDrawerDragZoneRef}
              data-tour="mobile-lead-drawer-drag-zone"
              onTouchStart={onLeadDrawerDragTouchStart}
              onTouchEnd={onLeadDrawerDragTouchEnd}
              onPointerDown={onLeadDrawerDragPointerDown}
              onPointerUp={onLeadDrawerDragPointerUp}
              onPointerCancel={onLeadDrawerDragPointerUp}
              sx={{
                flexShrink: 0,
                px: 2.25,
                pt: 1.25,
                pb: 0.5,
                touchAction: "none",
                cursor: drawerDragging ? "grabbing" : "grab",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
            >
            <Box
              sx={{
                width: 42,
                height: 4,
                borderRadius: 999,
                bgcolor: "rgba(120,120,120,0.35)",
                mx: "auto",
                mb: 1.5,
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 0.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: "-0.2px",
                  }}
                >
                  Lead Details
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mt: 0.35,
                  }}
                >
                  View and update lead info
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showLeadDrawerExpandedUi && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={drawerSaving ? null : <SaveIcon sx={{ fontSize: 16 }} />}
                    disabled={drawerSaving}
                    onPointerDown={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onClick={handleDrawerSave}
                    sx={{
                      minWidth: "auto",
                      px: 1.35,
                      py: 0.7,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "0 6px 16px rgba(26,115,232,0.24)",
                    }}
                  >
                    {drawerSaving ? "Saving…" : "Save"}
                  </Button>
                )}

                <IconButton
                  size="small"
                  onClick={closeLeadDrawer}
                  onPointerDown={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 2.25,
                pb: 2.25,
              }}
            >
            {selectedLeadForDrawer && (
              <>
                <Card
                  elevation={0}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    border: "1px solid rgba(0,0,0,0.06)",
                    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FAFBFC",
                    mb: 1.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 17,
                      fontWeight: 700,
                      lineHeight: 1.15,
                    }}
                  >
                    {selectedLeadForDrawer.first_name} {selectedLeadForDrawer.last_name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1.25,
                      letterSpacing: "-0.2px",
                      color: "text.primary",
                      mb: 0.35,
                    }}
                  >
                    {selectedLeadForDrawer.address || "No address available"}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                      lineHeight: 1.3,
                      mb: 1.1,
                    }}
                  >
                    {[
                      selectedLeadForDrawer.city,
                      formatStateAbbr(selectedLeadForDrawer.state),
                      selectedLeadForDrawer.zip_code,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.35,
                      mt: 0.2,
                    }}
                  >
                    <Box>
                      <Typography sx={drawerStatLabelSx}>Date Sold</Typography>
                      <Typography sx={drawerStatValueSx}>
                        {selectedLeadForDrawer.sold_date
                          ? new Date(selectedLeadForDrawer.sold_date).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={drawerStatLabelSx}>Price</Typography>
                      <Typography sx={drawerStatValueSx}>
                        {selectedLeadForDrawer.price
                          ? `$${Number(selectedLeadForDrawer.price).toLocaleString()}`
                          : "—"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={drawerStatLabelSx}>Square Feet</Typography>
                      <Typography sx={drawerStatValueSx}>
                        {selectedLeadForDrawer.square_feet
                          ? Number(selectedLeadForDrawer.square_feet).toLocaleString()
                          : "—"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={drawerStatLabelSx}>Status</Typography>
                      <Chip
                        label={selectedLeadForDrawer.status || "New"}
                        size="small"
                        sx={{
                          mt: 0.35,
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 12,
                          height: "auto",
                          py: 0.25,
                          bgcolor: getStatusChipStyle(selectedLeadForDrawer.status).bg,
                          color: getStatusChipStyle(selectedLeadForDrawer.status).color,
                          border: "none",
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    </Box>
                  </Box>
                </Card>

                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.35 }}>
                    Notes
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "text.primary" }}>
                    {selectedLeadForDrawer.notes || "No notes"}
                  </Typography>
                </Box>

                {canViewAdditionalNotes && (
                  <Box sx={{ mb: 1.75 }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.35 }}>
                      Additional Notes
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.primary" }}>
                      {selectedLeadForDrawer.additional_notes || "—"}
                    </Typography>
                  </Box>
                )}

                <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box>
                      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="First name"
                          value={drawerEditData.first_name}
                          onChange={(e) => setDrawerEditData({ ...drawerEditData, first_name: e.target.value })}
                          sx={compactFieldSx}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Last name"
                          value={drawerEditData.last_name}
                          onChange={(e) => setDrawerEditData({ ...drawerEditData, last_name: e.target.value })}
                          sx={compactFieldSx}
                        />
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        placeholder="Email"
                        value={drawerEditData.email}
                        onChange={(e) => setDrawerEditData({ ...drawerEditData, email: e.target.value })}
                        sx={{ ...compactFieldSx, mb: 1 }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        type="tel"
                        placeholder="(xxx) xxx-xxxx"
                        value={formatPhone(drawerEditData.phone)}
                        onChange={(e) => setDrawerEditData({ ...drawerEditData, phone: formatPhone(e.target.value) })}
                        sx={{ ...compactFieldSx, mb: 1 }}
                      />
                      <FormControl id="leads-mobile-sms-opt-in" fullWidth size="small" sx={{ mb: 1 }} disabled={!(drawerEditData.phone || "").trim()}>
                        <InputLabel required={(drawerEditData.phone || "").trim().length > 0} sx={{ fontSize: 13 }}>SMS/Email Opt-In</InputLabel>
                        <Select
                          value={drawerEditData.sms_email_opt_in ?? ""}
                          label="SMS/Email Opt-In"
                          onChange={(e) => setDrawerEditData({ ...drawerEditData, sms_email_opt_in: e.target.value })}
                          MenuProps={{
                            disablePortal: false,
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            slotProps: {
                              root: { sx: { zIndex: 5000 } },
                              paper: {
                                sx: {
                                  maxHeight: 280,
                                  zIndex: 5000,
                                  borderRadius: 3,
                                  boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.1)",
                                  bgcolor: "background.paper",
                                  overflow: "hidden",
                                },
                              },
                            },
                            MenuListProps: {
                              sx: {
                                py: 0.5,
                                overflowY: "auto",
                                maxHeight: 264,
                              },
                            },
                          }}
                          sx={compactFieldSx}
                        >
                          <MenuItem value=""><em>—</em></MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                        {!(drawerEditData.phone || "").trim() && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: 11 }}>
                            Enter a phone number to set opt-in status
                          </Typography>
                        )}
                      </FormControl>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <Select
                          value={drawerEditData.status || "New"}
                          onChange={(e) => setDrawerEditData({ ...drawerEditData, status: e.target.value })}
                          MenuProps={{
                            disablePortal: false,
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            slotProps: {
                              root: { sx: { zIndex: 5000 } },
                              paper: {
                                sx: {
                                  maxHeight: 280,
                                  zIndex: 5000,
                                  borderRadius: 3,
                                  boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.1)",
                                  bgcolor: "background.paper",
                                  overflow: "hidden",
                                },
                              },
                            },
                            MenuListProps: {
                              sx: {
                                py: 0.5,
                                overflowY: "auto",
                                maxHeight: 264,
                              },
                            },
                          }}
                          sx={{
                            ...compactFieldSx,
                            "& .MuiSelect-select": {
                              padding: "11px 14px",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          {DRAWER_STATUS_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12, py: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <LeadStatusIcon status={opt.value} size={18} />
                                  {opt.label}
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {drawerEditData.status === "Follow Up Needed" && (
                        <TextField
                          id="leads-mobile-follow-up-date"
                          size="small"
                          fullWidth
                          type="date"
                          label="Follow-up date"
                          InputLabelProps={{ shrink: true }}
                          value={drawerEditData.followUpDate || ""}
                          onChange={(e) =>
                            setDrawerEditData({ ...drawerEditData, followUpDate: e.target.value })
                          }
                          sx={{ ...compactFieldSx, mb: 1 }}
                        />
                      )}
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Notes"
                        value={drawerEditData.notes}
                        onChange={(e) => setDrawerEditData({ ...drawerEditData, notes: e.target.value })}
                        sx={compactMultilineFieldSx}
                      />
                      {showLeadDrawerExpandedUi && (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={
                            drawerProposalAction.icon === "timer" ? (
                              <AccessTimeIcon />
                            ) : drawerProposalAction.icon === "check" ? (
                              <CheckIcon />
                            ) : drawerProposalAction.icon === "declined" ? (
                              <HighlightOffIcon />
                            ) : (
                              <AddIcon />
                            )
                          }
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={openProposalFromDrawer}
                          sx={{
                            mt: 1.75,
                            borderRadius: "14px",
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "1rem",
                            bgcolor: drawerProposalAction.bgcolor,
                            boxShadow: "none",
                            "&:hover": { bgcolor: drawerProposalAction.hover, boxShadow: "none" },
                          }}
                        >
                          {drawerProposalAction.label}
                        </Button>
                      )}
                    </Box>
                </>
              </>
            )}
            </Box>

            {showLeadDrawerCompactFooter && selectedLeadForDrawer && (
              <Box
                sx={{
                  p: 2.25,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                  bgcolor: "background.paper",
                }}
              >
                <Stack spacing={1.1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    data-tour="mobile-drawer-add-route"
                    disabled={selectedLeadForDrawer.in_other_rep_route}
                    onClick={() => {
                      if (selectedLeadForDrawer.in_other_rep_route) {
                        toast.info("This lead is already in another sales rep's route");
                        return;
                      }
                      const alreadySelected = selectedLeads.some((l) => l.id === selectedLeadForDrawer.id);
                      const isAdding = !alreadySelected;
                      if (boxSelectMode && isAdding && toggleBoxSelect) {
                        toggleBoxSelect();
                      }
                      if (setSelectedLeads) {
                        setSelectedLeads((prevSelected) =>
                          alreadySelected
                            ? prevSelected.filter((l) => l.id !== selectedLeadForDrawer.id)
                            : [...prevSelected, selectedLeadForDrawer]
                        );
                      }
                      toast.success(alreadySelected ? "Removed from route" : "Added to route");
                      setTimeout(() => setSelectedLeadForDrawer(null), 50);
                    }}
                    sx={{
                      borderRadius: 3,
                      py: 1.1,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "0 6px 16px rgba(26,115,232,0.24)",
                    }}
                  >
                    {selectedLeads.some((l) => l.id === selectedLeadForDrawer.id)
                      ? "Remove from Route"
                      : "Add to Route"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    startIcon={<NavigationIcon />}
                    onClick={() => handleNavigateLead(selectedLeadForDrawer)}
                    sx={{
                      borderRadius: 3,
                      py: 1.05,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Navigate
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    data-tour="mobile-drawer-lead-details-btn"
                    startIcon={<CalendarTodayIcon />}
                    onClick={() => setDrawerExpanded(true)}
                    sx={{
                      borderRadius: 3,
                      py: 1.05,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Update lead
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Drawer>
      )}

      <Drawer
        anchor="bottom"
        open={navChooserOpen}
        onClose={handleNavChooserClose}
        sx={{
          zIndex: 7000,
          "& .MuiDrawer-paper": {
            zIndex: 7000,
          },
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: "min(88dvh, 520px)",
            pb: "env(safe-area-inset-bottom, 0px)",
            overflow: "hidden",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(15,23,42,0.98)"
                : "rgba(255,255,255,0.98)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 -10px 30px rgba(0,0,0,0.45)"
                : "0 -10px 30px rgba(0,0,0,0.14)",
            borderTop: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(120,120,120,0.35)",
              mx: "auto",
              mt: 1.25,
              mb: 1,
            }}
          />
          <Box
            sx={{
              px: 2.25,
              pt: 0.5,
              pb: 1.35,
              borderBottom: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(248,250,252,0.72)",
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              Choose navigation app
            </Typography>

            <Typography
              sx={{
                mt: 0.45,
                fontSize: 13,
                color: "text.secondary",
              }}
            >
              Pick how you want to open directions
            </Typography>
          </Box>

          <Box sx={{ px: 2.25, py: 1.75 }}>
            <Stack spacing={1.1}>
              {navProviderOptions.map((provider) => (
                <Button
                  key={provider}
                  onClick={() => handleChooseNavProvider(provider)}
                  fullWidth
                  sx={{
                    justifyContent: "center",
                    borderRadius: "18px",
                    py: 1.25,
                    px: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "primary.main",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(255,255,255,0.88)",
                    border: "1px solid",
                    borderColor: "rgba(37,99,235,0.28)",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 6px 16px rgba(0,0,0,0.25)"
                        : "0 6px 16px rgba(15,23,42,0.06)",
                    "&:hover": {
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(37,99,235,0.12)"
                          : "rgba(37,99,235,0.06)",
                      borderColor: "primary.main",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {providerLabel(provider)}
                </Button>
              ))}

              <Box
                sx={{
                  mt: 0.4,
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(248,250,252,0.88)",
                  px: 1.25,
                  py: 0.5,
                }}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    width: "100%",
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: 15,
                      fontWeight: 500,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={rememberNavChoice}
                      onChange={(e) => setRememberNavChoice(e.target.checked)}
                    />
                  }
                  label="Remember my choice"
                />
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              px: 2.25,
              py: 1.4,
              borderTop: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(248,250,252,0.72)",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={handleNavChooserClose}
              sx={{
                borderRadius: "14px",
                py: 0.9,
                px: 1.8,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      <RequiredChoiceDialog
        open={mobileRequiredChoice === "opt-in"}
        onClose={() => setMobileRequiredChoice(null)}
        eyebrow="Save lead"
        title="Opt-in required"
        message="Choose SMS/Email Opt-In before saving a lead that has a phone number."
        actionLabel="Choose opt-in"
        zIndex={8000}
        onAction={() => {
          setMobileRequiredChoice(null);
          focusRequiredField("leads-mobile-sms-opt-in");
        }}
      />
      <RequiredChoiceDialog
        open={mobileRequiredChoice === "follow-up"}
        onClose={() => setMobileRequiredChoice(null)}
        eyebrow="Save lead"
        title="Follow-up date required"
        message="Choose a follow-up date before saving this lead."
        actionLabel="Choose date"
        zIndex={8000}
        onAction={() => {
          setMobileRequiredChoice(null);
          focusRequiredField("leads-mobile-follow-up-date");
        }}
      />

      {/* ================= SAVE ROUTE MODAL ================= */}
      {setShowSaveModal && token && (
        <SaveRouteModal
          open={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          route={routeCoordinates}
          selectedLeads={selectedLeads}
          token={token}
        />
      )}
    </Box>
  );
};

export default LeadsMobileMapView;